import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchCampaignStatus } from "@/modules/administration/features/Stochastic/CampaignStatus/api/fetchCampaignStatus/fetchCampaignStatusApi";
import {
  CampaignStatusItem,
  FetchCampaignStatusRequest,
  FetchCampaignStatusResponse,
} from "@/modules/administration/features/Stochastic/CampaignStatus/api/fetchCampaignStatus/types";
import { AppDispatch, AppThunk } from "@/app/store";

interface SubscriptionBillingDataState {
  campaigns: CampaignStatusItem[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: SubscriptionBillingDataState = {
  campaigns: [],
  totalCount: 0,
  loading: false,
  error: null,
};

const subscriptionBillingDataSlice = createSlice({
  name: "subscriptionBillingData",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCampaigns: (
      state,
      action: PayloadAction<{
        campaigns: CampaignStatusItem[];
        totalCount: number;
      }>,
    ) => {
      state.campaigns = action.payload.campaigns;
      state.totalCount = action.payload.totalCount;
    },
  },
});

export const { setLoading, setError, setCampaigns } =
  subscriptionBillingDataSlice.actions;

export const fetchSubscriptionBillingDataAction =
  (requestParams: FetchCampaignStatusRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response: FetchCampaignStatusResponse =
        await fetchCampaignStatus(requestParams);

      dispatch(
        setCampaigns({
          campaigns: response.data,
          totalCount: response.meta?.totalCount ?? 0,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch subscription campaigns"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export default subscriptionBillingDataSlice.reducer;
